/**
 * @file LoginFormViewModel.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Subject } from "rxjs";
import { ApiError } from "../../../services/models/ApiError";
import { AppAccessToken } from "../../../services/models/AppAccessToken";
import { GetClinicResponse } from "../../../services/models/GetClinicResponse";
import { Clinic } from "../../../services/models/Clinic";
import { BaseConsentViewModel } from "../../common/viewmodel/BaseConsentViewModel";

/**
 * A class definition for LoginFormViewModel
 */
export class LoginFormViewModel extends BaseConsentViewModel {

    constructor() {
        super();
    }

    /**
     * rxjs Variables for API
     */
    onLoggedIn = new Subject<AppAccessToken>();
    onLoggedInError = new Subject<ApiError>();

    onClinicLoaded = new Subject<Clinic[]>();
    onClinicError = new Subject<ApiError>();

    onLoadAuthorizeURL = new Subject<string>();
    onLoadAuthorizeURLError = new Subject<ApiError>();
    
    /**
     * Logins api
     * @param username string
     * @param password string
     */
    async login(username: string, password: string) {
        try {
            const userToken = await this.apiService.login(username, password);
            this.onLoggedIn.next(userToken as AppAccessToken);
        } catch(error: unknown) {
            this.logger.verbose(`Error while login - ${error}`, LoginFormViewModel.name);
            this.onLoggedInError.next((error as any) as ApiError);
        }
    }

    /**
     * Get clinics
     * @param accessToken string
     */
    async getClinics(accessToken: string) {
        try {
            const getClinicResponse: GetClinicResponse = await this.apiService.getClinicList(accessToken);
            this.onClinicLoaded.next(getClinicResponse.rows!!); // eslint-disable-line
        } catch(error: unknown){
            this.onClinicError.next((error as any) as ApiError);
        }
    }

    /**
     * Gets authorize URL
     */
    async getAuthorizeURL() {
        try{
            const ssoUrl= await this.apiService.getAuthenticationRoute();
            this.onLoadAuthorizeURL.next(ssoUrl);
        } catch(error: unknown) {
           this.onLoadAuthorizeURLError.next((error as any) as ApiError);
        }
    }
    
}